import { type Room } from "matrix-js-sdk/src/matrix";

import { type TagID } from "../../../room-list-v3/skip-list/tag";

/**
 * Represents a tag sorting algorithm.
 */
export interface IAlgorithm {
    /**
     * Sorts the given rooms according to the sorting rules of the algorithm.
     * @param {Room[]} rooms The rooms to sort.
     * @param {TagID} tagId The tag ID in which the rooms are being sorted.
     * @returns {Room[]} Returns the sorted rooms.
     */
    sortRooms(rooms: Room[], tagId: TagID): Room[];

    /**
     * Determines the correct index at which to insert a new room
     * into an already-sorted list of rooms, according to the algorithm's sorting rules.
     * @param sortedRooms The already sorted list of rooms.
     * @param newRoom The room to insert.
     * @param tagId The tag ID in which the rooms are being sorted.
     * @returns The index where the room should be inserted.
     */
    getInsertIndex(sortedRooms: Room[], newRoom: Room, tagId: TagID): number;

}

/**
 * Finds the index at which to insert an item into a sorted array to maintain order.
 * Works like Array.prototype.sort() comparator semantics:
 * - compareFn(a, b) < 0 means a comes before b
 * - compareFn(a, b) > 0 means a comes after b
 * - compareFn(a, b) = 0 means equal
 */
export function binarySearchInsertIndex<T>(
    sortedItems: T[],
    newItem: T,
    compareFn: (a: T, b: T) => number,
): number {
    let low = 0;
    let high = sortedItems.length;

    while (low < high) {
        const mid = Math.floor((low + high) / 2);
        const cmp = compareFn(newItem, sortedItems[mid]);

        if (cmp < 0) {
            // item should come before mid
            high = mid;
        } else {
            // item should come after (or equal)
            low = mid + 1;
        }
    }

    return low;
}
